/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
// @vendors
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// @context
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// @services
import UserService from '../../services/user.service';

// @components
import PendingVerificationModal from '../../components/PendingVerificationModal';
import AuthModal from '../../components/AuthModal';

// @routes
import { PathRoutes } from '../../components/routes/PathRoutes';

// @utils
import { USER_ROLES } from '../../utils/ConstantsUtils';

const Navbar: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { currentUser, signOut, userInfo } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();

  // Función para obtener el perfil del usuario desde el estado global o el backend como fallback
  const fetchUserProfile = async () => {
    if (userInfo && userInfo.token) {
      if (userInfo.profileImage) {
        try {
          setProfileImageUrl(userInfo.profileImage);
        } catch (error) {
          setProfileImageUrl(userInfo.profileImage);
        }
      } else {
        setProfileImageUrl(null);
      }

      setUserRole(localStorage.getItem('user_role') || 'user');
      setIsLoadingProfile(false);
      return;
    }
    const authToken = localStorage.getItem('auth_token');
    if (!currentUser && !authToken) return;
    //handleCurrentUser();
  };

  const handleCurrentUser = async () => {
    try {
      setIsLoadingProfile(true);
      const userProfile = await UserService.getCurrentUserProfile();
      if (userProfile.state) localStorage.setItem('user_state', userProfile.state);

      if (userProfile.role) {
        setUserRole(userProfile.role);
        localStorage.setItem('user_role', userProfile.role);
      }

      if (userProfile.profilePictureUrl) {
        setProfileImageUrl(userProfile.profilePictureUrl);
        localStorage.setItem('userProfileImage', userProfile.profilePictureUrl);
      } else {
        const savedProfileImage = localStorage.getItem('userProfileImage');
        if (savedProfileImage) {
          setProfileImageUrl(savedProfileImage);
        }
      }
    } catch (error) {
      const savedProfileImage = localStorage.getItem('userProfileImage');
      if (savedProfileImage) setProfileImageUrl(savedProfileImage);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Escuchar los eventos personalizados de autenticación
  useEffect(() => {
    const handleAuthSuccess = async (event: Event) => {
      const savedProfileImage = localStorage.getItem('userProfileImage');
      if (savedProfileImage) setProfileImageUrl(savedProfileImage);
      fetchUserProfile();
      const authToken = localStorage.getItem('auth_token');
      if (authToken && isAuthModalOpen) setIsAuthModalOpen(false);
    };

    // Manejador para el evento de cierre de sesión
    const handleAuthLogout = () => {
      setProfileImageUrl(null);
      setUserRole(null);
    };

    // Agregar event listeners
    window.addEventListener('auth-success', handleAuthSuccess as EventListener);
    window.addEventListener('auth-logout', handleAuthLogout as EventListener);

    // Limpiar los event listeners cuando el componente se desmonte
    return () => {
      window.removeEventListener('auth-success', handleAuthSuccess as EventListener);
      window.removeEventListener('auth-logout', handleAuthLogout as EventListener);
    };
  }, [userInfo]);

  // Cargar el perfil del usuario cuando se monte el componente y cuando cambie currentUser
  useEffect(() => {
    const authToken = localStorage.getItem('auth_token');
    const savedState = localStorage.getItem('user_state');
    const savedProfileImage = localStorage.getItem('userProfileImage');
    const savedRole = localStorage.getItem('user_role');
    if (savedProfileImage) {
      setProfileImageUrl(savedProfileImage);
    }

    if (savedRole) {
      setUserRole(savedRole);
    } else {
      setUserRole(null);
    }

    if (authToken && savedState === 'active') {
      fetchUserProfile();
    } else {
      setUserRole(null);
    }
  }, []);

  // Este useEffect se encarga de verificar si hay un modal pendiente que mostrar
  useEffect(() => {
    if (isLoggingOut) return;
    if (userInfo && userInfo.token) {
      setProfileImageUrl(userInfo.profileImage);
      const savedRole = localStorage.getItem('user_role');
      if (savedRole) setUserRole(savedRole);
      else setUserRole(null);
      setIsLoadingProfile(false);
      return;
    }

    const authToken = localStorage.getItem('auth_token');
    const savedState = localStorage.getItem('user_state');
    const savedRole = localStorage.getItem('user_role');

    // Actualizar el rol del usuario solo si hay un token válido y un rol en localStorage
    if (savedRole && authToken) setUserRole(savedRole);
    else setUserRole(null);

    // Si hay un token y el usuario está activo, cargar el perfil
    if (savedState && authToken) fetchUserProfile();
    else if (currentUser) fetchUserProfile();

    // Si hay una bandera para mostrar el modal de verificación pendiente
    const showVerification = localStorage.getItem('show_pending_verification') === 'true';
    if (showVerification) setShowPendingModal(true);
  }, [currentUser, userInfo, isLoggingOut]);

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();

      setProfileImageUrl(null);
      setUserRole(null);
      // Emitir evento de logout para que otros componentes se actualicen
      window.dispatchEvent(new Event('auth-logout'));
      clearLocalStorage();
      navigate('/');
    } catch (error) {
    } finally {
      setIsLoggingOut(false);
    }
  };

  const clearLocalStorage = () => {
    localStorage.clear();
  };

  // Función para cerrar el modal de verificación pendiente
  const closePendingModal = () => {
    setShowPendingModal(false);
    localStorage.removeItem('show_pending_verification');
  };

  const getTitleDashboard = () => {
    if (userRole === USER_ROLES.INMOBILIARIA) return 'Dashboard IN';
    if (userRole === USER_ROLES.COMPANY_PARTNER) return 'Dashboard Partner';
    return 'Dashboard Unidad';
  }

  const getRouteToGo = () => {
    if (userRole === USER_ROLES.INMOBILIARIA) return PathRoutes.inmobiliaria;
    if (userRole === USER_ROLES.COMPANY_PARTNER) return PathRoutes.companyPartner;
    return PathRoutes.userAdminUnit;
  }

  const hasAccessToDashboard = () => {
    const roles = [USER_ROLES.INMOBILIARIA, USER_ROLES.COMPANY_PARTNER, USER_ROLES.USER_ADMIN_UNIT];
    return roles.includes(userRole || '');
  }

  const scrollToElement = (e: React.MouseEvent<HTMLAnchorElement>, elementId: string) => {
    e.preventDefault();
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  return (
    <>
      <nav className="py-4 px-6 md:px-12 flex justify-between items-center">
        <div
          className="text-2xl font-bold text-xervix-primary cursor-pointer hover:opacity-80 transition-opacity"
          title="Click para probar endpoint de MailerSend"
        >
          XERVIX
        </div>
        <div className="hidden md:flex space-x-8">
          <a
            href="#home"
            onClick={(e) => scrollToElement(e, 'home')}
            className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
          >
            Home
          </a>
          <a
            href="#que-hacemos"
            onClick={(e) => scrollToElement(e, 'que-hacemos')}
            className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
          >
            Que hacemos
          </a>
          <a
            href="#como-funciona"
            onClick={(e) => scrollToElement(e, 'como-funciona')}
            className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
          >
            Como funciona
          </a>
          <a
            href="#contacto"
            onClick={(e) => scrollToElement(e, 'contacto')}
            className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
          >
            Contacto
          </a>
          {userRole === USER_ROLES.COMPANY && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(PathRoutes.dashboard);
              }}
              className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
            >
              Dashboard
            </a>
          )}

          {hasAccessToDashboard() && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(getRouteToGo());
              }}
              className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
            >
              {getTitleDashboard()}
            </a>
          )}

          {userRole === USER_ROLES.ADMIN_PH && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(PathRoutes.inmobiliaria);
              }}
              className="hover:text-xervix-primary text-xervix-text-primary dark:text-xervix-text-secondary-dark"
            >
              Dashboard PH
            </a>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {/* Botón de cambio de tema */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md bg-xervix-bg-tertiary hover:bg-gray-300 dark:bg-xervix-bg-tertiary-dark dark:hover:bg-gray-600 transition-colors duration-300"
            aria-label={`Cambiar a modo ${theme === 'dark' ? 'claro' : 'oscuro'}`}
          >
            {theme === 'dark' ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <div className="flex items-center">

            {(currentUser || localStorage.getItem('auth_token')) ? (
              <div className="flex items-center">
                <div className="flex items-center hover:opacity-80 transition-opacity cursor-pointer mr-4">
                  <span className="mr-2 text-xervix-text-primary dark:text-xervix-text-primary-dark">
                    {currentUser?.displayName?.split(' ')[0] || 'Usuario'}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-gray-800 font-bold overflow-hidden">
                    {isLoadingProfile ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : profileImageUrl ? (
                      // Usar la imagen obtenida del backend o localStorage
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          setProfileImageUrl(null);
                        }}
                      />
                    ) : currentUser?.photoURL ? (
                      // Si no hay imagen en el backend ni localStorage, usar la imagen de Google
                      <img
                        src={currentUser.photoURL}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onLoad={() => {
                          if (!profileImageUrl && currentUser?.photoURL) {
                            localStorage.setItem('userProfileImage', currentUser.photoURL);
                            setProfileImageUrl(currentUser.photoURL);
                          }
                        }}
                      />
                    ) : (
                      // Si no hay ninguna imagen, mostrar la inicial del nombre
                      <span>{currentUser?.displayName?.[0] || 'U'}</span>
                    )}
                  </div>
                </div>

                {/* Botón de logout */}
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Cerrar sesión"
                >
                  {isLoggingOut ? (
                    <svg className="w-5 h-5 animate-spin text-gray-600 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={openAuthModal}
                className="px-4 py-2 rounded-md bg-xervix-primary text-white hover:bg-opacity-90 transition-colors"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Modal de autenticación */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        handleCurrentUser={handleCurrentUser}
      />

      {/* Modal de verificación pendiente */}
      <PendingVerificationModal
        isOpen={showPendingModal}
        onClose={closePendingModal}
      />
    </>
  );
};

export default Navbar;
